"use client";
import { RefObject, useRef, useState } from "react";
import { useInView } from "@/components/Animation";
import styles from "./Video.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import dynamic from "next/dynamic";
import { type MediaPlayerInstance } from "@vidstack/react";
import { HeaderVideosType } from "@/types/PageType";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";

const VidstackPlayer = dynamic(
  () => import("../VidstackPlayer/VidstackPlayer"),
  { ssr: false },
);

export const AudioIcon = ({
  isMuted,
  player,
  getIcon,
}: {
  isMuted: boolean;
  player: RefObject<MediaPlayerInstance>;
  getIcon: (key: string) => string;
}) => {
  const { getLabel } = useGlobalContext();
  return (
    <button
      className={`${isMuted ? styles["muted"] : ""} ${styles["audio-btn"]} audio-btn button-reset`}
      onClick={(e) => {
        e.stopPropagation();
        const c = player.current;
        if (c) {
          c.muted = !isMuted;
        }
      }}
      role="switch"
      aria-checked={!isMuted}
      aria-label={
        isMuted ? getLabel("unmute", "Unmute") : getLabel("mute", "Mute")
      }
    >
      <CustomSVG
        width={32}
        height={32}
        src={isMuted ? getIcon("audioOff") : getIcon("audioOn")}
        wrapper="span"
        aria-hidden="true"
        className="is-flex"
      />
    </button>
  );
};

export const InternalVideo = ({
  autoplay,
  loop = true,
  muted: mutedProps,
  video_decorative = false,
  singlePageUrl,
  video,
}: {
  autoplay: boolean;
  controls?: boolean;
  loop?: boolean;
  muted: boolean;
  video_decorative?: boolean;
  singlePageUrl?: string;
  video: HeaderVideosType;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const playerInView = useInView(ref, { triggerOnce: true });
  return (
    // we need the video-container-height so make sure that the empty div takes enough height to trigger useInView and IntersectionObserver in useScrollAnimation
    <div ref={ref} className="video-container-height">
      {playerInView && video.internes_video_desktop && (
        <VidstackPlayer
          autoplay={autoplay}
          loop={loop}
          muted={mutedProps}
          video_decorative={video_decorative}
          singlePageUrl={singlePageUrl}
          video={video}
        />
      )}
    </div>
  );
};

export const PlayPauseButton = ({
  isPlaying,
  player,
  getIcon,
  setShowcontrols,
  video_decorative = false,
}: {
  isPlaying: boolean;
  player: React.RefObject<MediaPlayerInstance>; // Replace 'any' with your MediaPlayerInstance type if available
  getIcon: (key: string) => string;
  setShowcontrols: React.Dispatch<React.SetStateAction<boolean>>;
  video_decorative?: boolean;
}) => {
  const [playing, setPlaying] = useState(isPlaying);
  const { getLabel } = useGlobalContext();
  const handlePlayPause = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const c = player.current;
    if (c) {
      if (playing) {
        c.pause(); // Assuming your MediaPlayerInstance has a pause method
      } else {
        c.play(); // Assuming your MediaPlayerInstance has a play method
      }
      setPlaying(!playing);
      if (!video_decorative) setShowcontrols(!playing);
    }
  };

  return (
    <button
      className={`${styles["play-pause-btn"]} button-reset play-pause-btn`}
      onClick={handlePlayPause}
      aria-label={
        playing ? `${getLabel("pause")} Button` : `${getLabel("play")} Button`
      }
    >
      <CustomSVG
        src={playing ? getIcon("pause") : getIcon("play")}
        width={32}
        height={32}
        wrapper="span"
        aria-hidden
        className="is-flex"
      />
    </button>
  );
};
